import { useState, useMemo, useEffect, useRef } from "react";
import Input from "../components/shared/Input";
import { useForm, useFormState, useWatch } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useProject, useUser, usePrint } from "../hooks/useQueries";
import { Tabs, TabButton } from "../components/shared/Tabs";
import ProjectHeader from "../components/project/ProjectHeader";
import ProjectLists from "../components/project/ProjectLists";
import Modal from "react-modal";
import ProjectItemsGrid from "../components/project/ProjectItemsGrid";
import { toast } from "react-toastify";
import fileDownload from "js-file-download";
import axios from "../axios";
import { generalStore } from "../stores/userStore";
import ProjectDiscount from "../components/project/ProjectDiscount";
import CustomItem from "../components/project/CustomItem";
import NewModal from "../components/shared/NewModal";
import PrintOptions from "../components/shared/PrintOptions";
import EngineerNotesModal from "../components/project/EngineerNotesModal";
import { queryClient } from "../react-query/queryClient";
import { projectQueryKeys } from "../react-query/queryConstants";
import OfferModal from "../components/project/OfferModal";
import { useOffers } from "../hooks/useOffers";

const Project = () => {
    const { user } = useUser();
    const [projectLocation, setProjectLocation] = useState();
    const [setHideLoading] = generalStore((state) => [
        state.hideLoading,
        state.setHideLoading,
    ]);

    const {
        project,
        setProject,
        projectItems,
        saveProject,
        projectLists,
        openProject,
        newProject,
        projectLocations,
        cities,
        projectStatus,
        competitors,
        poStatus,
        lostReasons,
        consultants,
        salesEngineers,
        updateProjectDiscounts,
        updateProjectAddons,
        addCustomItem,
        updateProjectDetails,
        refreshProjectPrice,
        discountLimit,
        checkProjectDiscountWithLocation,
        projectItemsSummary,
        isFetchinGetProjectItems,
    } = useProject(projectLocation);

    const [modalIsOpen, setIsOpen] = useState(false);
    const [discountModalIsOpen, setDiscountModalIsOpen] = useState(false);
    const [customItemModalIsOpen, setCustmItemModalIsOpen] = useState(false);
    const [projectDiscount, setProjectDiscount] = useState();
    const [salesOfficeMargin, setSalesOfficeMargin] = useState();

    const [customModal, setCustomModal] = useState();
    const [customPrice, setCustomPrice] = useState();
    const [customQty, setCustomQty] = useState(1);

    const [printOptionModalIsOpen, setPrintOptionModalIsOpen] = useState();

    // ── Engineer notes state ────────────────────────────────────────────────
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isImportingDraft, setIsImportingDraft] = useState(false);
    const [isDraft, setIsDraft] = useState(false);
    const fileInputRef = useRef(null);
    const [offerModalIsOpen, setOfferModalIsOpen] = useState(false);
    const [offerSummary, setOfferSummary] = useState("");
    const [offerData, setOfferData] = useState(null);

    const EMPTY_PROJECT_FORM = {
        quotationNumber: "",
        quotationDate: "",
        mepContractor: "",
        address: "",
        contactPerson: "",
        email: "",
        contact: "",
        projectDate: "",
        name: "",
        projectLocation: "",
        city: "",
        projectStatus: "",
        poStatus: "",
        reasonForLost: "",
        lostCompetitor: "",
        client: "",
        consultant: "",
        mainContractor: "",
        applicationEngineer: { name: "", telephone: "", email: "" },
        salesEngineer: { name: "", telephone: "", email: "" },
        salesManager: { name: "", telephone: "", email: "" },
        engineerNotes: [],
    };

    // Use the hook - this provides selectedOffers and setSelectedOffers
    const {
        loading: offersLoading,
        fetchOffers,
        loadProjectOffers,
        buildOfferSummary,
        getOfferIds,
        selectedOffers,
        setSelectedOffers
    } = useOffers();

    useEffect(function () {
        return () => {
            if (project) updateProjectDetails({ project: getValues() });
        };
    }, []);

    useEffect(function () { }, []);

    const handleOpenProject = (projectId, revision) => {
        setSelectedOffers({
            offerValidity: null, delivery: null, payment: null,
            incoterm: null, packing: null, warranty: null, forceMajeure: null
        });
        setOfferSummary("");
        setOfferData(null);
        openProject({ projectId: projectId, userId: user.id, Revision: revision });
        closeModal();
    };

    const handleNewProject = async () => {
        closeModal();

        try {
            const result = await newProject();
            const newProj = result?.data;
            reset(newProj ?? EMPTY_PROJECT_FORM);

            setProjectLocation(newProj?.projectLocation ?? "");
            setSelectedOffers({
                offerValidity: null,
                delivery: null,
                payment: null,
                incoterm: null,
                packing: null,
                warranty: null,
                forceMajeure: null
            });
            setOfferSummary("");
            setIsDraft(false);
        } catch (err) {
            console.error("Failed to create new project:", err);
            toast.error("Failed to create new project. Please try again.");
        }
    };

    const [printing, setPrinting] = useState(false);

    const onPrintSummary = () => {
        setPrinting(true);
        const printRequest = {
            Id: "-",
            PrintType: 1,
            Referrer: "Project",
            Culture: "en-GB",
            Drawings: null,
            showSummary: true,
            showTechnical: false,
            showDrawing: false,
        };

        axios
            .post(`/Print/Print`, printRequest, {
                responseType: "blob",
            })
            .then((response) => {
                fileDownload(response.data, "project_summary.pdf");
                setPrinting(false);
            })
            .catch(function (error) {
                setPrinting(false);
            });
    };

    const onPrint = (printType) => {
        setPrinting(true);

        const printRequest = {
            Id: "-",
            PrintType: 1,
            Referrer: "Project",
            Culture: "en-GB",
            Drawings: null,
            showSummary: selectedPrintOptions.includes("Summary"),
            showTechnical: selectedPrintOptions.includes("Technical"),
            showDrawing: selectedPrintOptions.includes("Drawing"),
            showSellingPriceBreakdown: selectedPrintOptions.includes(
                "SellingPriceBreakdown"
            ),
            showCostPriceBreakdown:
                selectedPrintOptions.includes("CostPriceBreakdown"),
        };

        setPrintOptionModalIsOpen(false);
        axios
            .post(`/Print/Print`, printRequest, {
                responseType: "blob",
            })
            .then((response) => {
                fileDownload(response.data, "project_summary.pdf");
                setPrinting(false);
            })
            .catch(function (error) {
                setPrinting(false);
            });
    };

    const validationSchema = yup.object().shape({
        name: yup.string().required("Required"),
        contact: yup.string().required("Required"),
        projectLocation: yup.string().required("Required"),
        projectStatus: yup.string().required("Required"),
        consultant: yup.string().required("Required"),
        salesEngineer: yup.object().shape({
            name: yup.string().required("Required"),
        }),
    });

    const {
        reset,
        register,
        formState: { errors },
        getValues,
        trigger,
        watch,
        setValue,
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: useMemo(() => EMPTY_PROJECT_FORM, []),
    });

    const hasProjectDiscount = Number(project?.discount) > 0;

    const hasLineItemDiscount = useMemo(() => {
        return (projectItemsSummary?.projectResultItems ?? []).some(
            (item) => Number(item?.lineItemDiscount) > 0
        );
    }, [projectItemsSummary?.projectResultItems]);

    const onError = (errors, e) => console.log(errors, e);

    useEffect(() => {
        if (project) {
            setProjectLocation(project.projectLocation);
        }
        reset(project);
    }, [project]);

    const pOStatus = watch("poStatus");
    const reasonForLost = watch("reasonForLost");
    const projectLocationWatch = watch("projectLocation");
    const engineerNotes = watch("engineerNotes");

    // Picking a Sales Engineer auto-fills their Tel + Email from the users list.
    const handleSalesEngineerChange = (name) => {
        const selected = salesEngineers?.find((u) => u.name === name);
        setValue("salesEngineer.telephone", selected?.telephone ?? "");
        setValue("salesEngineer.email", selected?.email ?? "");
    };

    const handleProjectLocationChange = async (locationCode) => {
        const pass = await checkProjectDiscountWithLocation(locationCode);

        if (!pass) {
            setProjectLocation((prev) => {
                setValue("projectLocation", prev);
                return prev;
            });
            toast("Discount not allowed");
            return;
        }
        setProjectLocation(locationCode);
        setValue("projectLocation", locationCode);
    };

    const saveAsDraftHandler = async () => {
        setIsSavingDraft(true);

        try {
            const response = await axios.post("/Project/export-draft", {
                ...getValues(),
                ...getOfferIds(selectedOffers),
            });

            const fieldsToStrip = [
                "services",
                "fatService",
                "cfdCarParkService",
                "cfdThermalService",
                "afterSalesService",
                "labTestingService",
                "servicesNotes",
            ];

            const draftPayload = { ...response.data };

            const projectKey = "project" in draftPayload ? "project" : "Project";

            if (draftPayload[projectKey]) {
                const strippedProject = { ...draftPayload[projectKey] };
                fieldsToStrip.forEach((field) => delete strippedProject[field]);
                draftPayload[projectKey] = strippedProject;
            }

            const formattedJson = JSON.stringify(draftPayload, null, 2);

            const projectName = getValues("name") || "draft";
            const safeName = projectName.replace(/[\\/:*?"<>|]/g, "_").trim() || "Draft";
            const date = new Date().toISOString().slice(0, 10);
            const fileName = `${safeName}_${date}.mepf`;

            const blob = new Blob([formattedJson], {
                type: "application/json",
            });

            fileDownload(blob, fileName);
            toast.success("Draft exported as a .mepf file.");
        } catch (err) {
            console.error("Export draft failed:", err);
            toast.error(err.response?.data?.message || "Failed to export draft. Please try again.");
        } finally {
            setIsSavingDraft(false);
        }
    };

    const triggerOpenDraftFile = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleDraftFileSelected = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.mepf')) {
            toast.error('Invalid file format. Please select a .mepf file.');
            e.target.value = '';
            return;
        }

        const reader = new FileReader();

        reader.onload = (event) => {
            let parsedData;
            try {
                parsedData = JSON.parse(event.target.result);
            } catch {
                toast.error('Invalid .mepf file: could not parse JSON.');
                e.target.value = '';
                return;
            }

            setIsImportingDraft(true);

            axios
                .post("/Project/import-draft", parsedData)
                .then((response) => {
                    queryClient.invalidateQueries([projectQueryKeys.getCurrentProject]);
                    queryClient.invalidateQueries([projectQueryKeys.getProjectItems]);

                    if (response.data?.data?.Project) {
                        reset(response.data.data.Project);
                        if (response.data.data.Project.projectLocation) {
                            setProjectLocation(response.data.data.Project.projectLocation);
                        }
                        setIsDraft(true);
                    }

                    toast.success(response.data?.data?.Message || `Draft loaded from ${file.name}`);
                })
                .catch((err) => {
                    console.error("Import draft failed:", err);
                    const serverMessage =
                        err?.response?.data?.message ||
                        err?.response?.data?.title ||
                        (typeof err?.response?.data === "string" ? err.response.data : null);

                    toast.error(
                        serverMessage
                            ? `Failed to load draft: ${serverMessage}`
                            : `Failed to load draft file. Please check the file format.`
                    );
                })
                .finally(() => {
                    setIsImportingDraft(false);
                    e.target.value = '';
                });
        };

        reader.onerror = () => {
            toast.error('Failed to read the file.');
            e.target.value = '';
        };

        reader.readAsText(file);
    };

    // Load offers when project loads
    useEffect(() => {
        const initOfferData = async () => {
            const data = await fetchOffers();
            if (data) setOfferData(data);
        };
        initOfferData();
    }, []);

    useEffect(() => {
        if (!project?.projectId) return;

        if (!offerData) return;

        const resolvedOffers = {
            //use == not === to handle number/string type mismatch from API
            offerValidity: offerData['Offer Validity']?.find(o => o.offerId == project.offerValidity) || null,
            delivery: offerData['Delivery']?.find(o => o.offerId == project.delivery) || null,
            payment: offerData['Payment']?.find(o => o.offerId == project.payment) || null,
            incoterm: offerData['Incoterm']?.find(o => o.offerId == project.incoterm) || null,
            packing: offerData['Packing']?.find(o => o.offerId == project.packing) || null,
            warranty: offerData['Warranty']?.find(o => o.offerId == project.warranty) || null,
            forceMajeure: offerData['Force Majeure']?.find(o => o.offerId == project.forceMajeure) || null,
        };

        setSelectedOffers(resolvedOffers);
        setOfferSummary(buildOfferSummary(resolvedOffers));

    }, [project?.projectId, offerData]);

    const openOfferModal = async () => {
        if (!offerData) {
            const data = await fetchOffers();
            if (data) {
                setOfferData(data);
            }
        }
        setOfferModalIsOpen(true);
    };

    // Handle saving offers - just updates state, auto-saves project
    const handleSaveOffers = (offers) => {
        setSelectedOffers(offers);
        const summary = buildOfferSummary(offers);
        setOfferSummary(summary);
        toast.success("Offers selected successfully");

        // Auto-save the project with the new offers
        if (project?.id) {
            const projectData = getValues();
            const offerIds = getOfferIds(offers);
            const projectWithOffers = {
                ...projectData,
                ...offerIds
            };

            saveProject({
                project: projectWithOffers,
                IncrementRevision: false,
                userId: user?.id,
            });
        }
    };

    // Handle closing offer modal
    const closeOfferModal = () => {
        setOfferModalIsOpen(false);
    };

    const columns = [
        {
            name: "Model",
            selector: (row) => row.ventilationUnitId,
            sortable: true,
        },
        {
            name: "Filter F/Velocity (m/s)",
            selector: (row) => row.velocity.toFixed(2),
            sortable: true,
        },
        {
            name: "ESP F/Velocity (m/s)",
            selector: (row) => row.espFaceVelocity.toFixed(2),
            sortable: true,
        },
        {
            name: "Area",
            selector: (row) => row.usableArea.toFixed(2),
            sortable: true,
        },
        {
            name: "Fan Model",
            selector: (row) => row.fanModel,
            sortable: true,
        },
        {
            name: "Fan Style",
            selector: (row) => row.fanStyle,
            sortable: true,
        },
        {
            name: "Volume (m/s)",
            selector: (row) => row.selectionVolume,
            sortable: true,
        },
        {
            name: "Pressure (Pa)",
            selector: (row) => row.selectionPressure.toFixed(2),
            sortable: true,
        },
        {
            name: "Efficiency (%)",
            selector: (row) => row.selectionStaticEfficiency.toFixed(2),
            sortable: true,
        },
    ];

    const [leftTabs, setLeftTabs] = useState([
        {
            id: "ProjectDetails",
            name: "Project Details",
            href: "",
            current: true,
            icon: null,
        },
    ]);

    const saveProjectHandler = async (saveType) => {
        const result = await trigger();
        if (result) {
            const projectData = getValues();

            const offerIds = getOfferIds(selectedOffers);
            const projectWithOffers = {
                ...projectData,
                ...offerIds
            };

            saveProject({
                project: projectWithOffers,
                IncrementRevision: saveType == "SaveIncrement",
                userId: user?.id,
            });
        } else {
            console.log(errors);
            toast("Please fill in required fields.");
        }
    };

    const openProjectListModal = () => {
        setIsOpen(true);
    };

    const onRefreshPrice = () => {
        refreshProjectPrice();
    };

    function closeModal() {
        setIsOpen(false);
        setDiscountModalIsOpen(false);
        setCustmItemModalIsOpen(false);
    }

    const openDiscountModal = () => {
        setProjectDiscount(project.discount);
        setSalesOfficeMargin(project.salesMargin);
        setDiscountModalIsOpen(true);
    };
    const openCustomItemModal = () => {
        setCustmItemModalIsOpen(true);
    };

    const onAddCustomItem = () => {
        addCustomItem({
            model: customModal,
            price: customPrice,
            quantity: customQty,
        });
        closeModal();
    };

    const [selectedPrintOptions, setSelectedPrintOptions] = useState([
        "Summary",
        "Technical",
        "Drawing",
    ]);

    const printOptions = () => {
        let options = [];
        options.push({ text: "Summary", value: "Summary" });
        options.push({ text: "Technical Data Sheet", value: "Technical" });
        options.push({ text: "Drawings", value: "Drawing" });
        if (user.userGroupId == 2 || (user.showSalePrice ?? false))
            options.push({ text: "Price Breakdown", value: "SellingPriceBreakdown" });
        if (user.userGroupId == 2 || (user.showCostPrice ?? false))
            options.push({
                text: "Cost Price Breakdown",
                value: "CostPriceBreakdown",
            });

        return options;
    };

    const onPrintClick = () => {
        setPrintOptionModalIsOpen(true);
    };

    const onPrintOptionsChanged = (checked, value) => {
        setSelectedPrintOptions((oldValues) => {
            if (checked) {
                oldValues.push(value);
                return [...oldValues];
            } else {
                return [...oldValues.filter((i) => i != value)];
            }
        });
    };

    const projectDiscountStyle = {
        content: {
            height: "30%",
            width: "50%",
            margin: "auto",
        },
    };
    const customItemStyle = {
        content: {
            height: "40%",
            width: "50%",
            margin: "auto",
        },
    };

    const date = new Date();
    const defaultValue = date.toLocaleDateString("en-CA");

    return (
        <form>
            <input
                type="file"
                ref={fileInputRef}
                accept=".mepf"
                style={{ display: "none" }}
                onChange={handleDraftFileSelected}
            />
            <div className="grid md:grid-cols-4 sm:grid-cols-1 gap-2">
                <div className="col-span-1">
                    <div className="overflow-hidden rounded-lg bg-white shadow mb-3">
                        <div className="rounded-lg bg-white col-span-1">
                            <Tabs
                                buttons={
                                    <TabButton isSelected={true}>Project Details</TabButton>
                                }
                            >
                                <div className="pt-0 overflow-hidden transition-[max-height] duration-200 ease-in">
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            size="sm"
                                            elementtype="input"
                                            label="Quotation"
                                            name="quotation"
                                            id="quotation"
                                            disabled
                                            type="text"
                                            readonly
                                            {...register("quotationNumber")}
                                            error={errors.title}
                                        />
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            size="sm"
                                            elementtype="input"
                                            label="MEP Contractor"
                                            name="mepContractor"
                                            id="mepContractor"
                                            type="text"
                                            {...register("mepContractor")}
                                            error={errors.title}
                                        />
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            size="sm"
                                            elementtype="input"
                                            label="Address"
                                            name="address"
                                            id="address"
                                            type="text"
                                            {...register("address")}
                                            error={errors.title}
                                        />
                                    </div>

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Contact Person"
                                            name="contactPerson"
                                            id="contactPerson"
                                            type="text"
                                            {...register("contactPerson", { required: true })}
                                            error={errors.contactPerson}
                                        />
                                    </div>

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Email"
                                            name="email"
                                            id="client"
                                            type="text"
                                            {...register("email")}
                                        />
                                    </div>

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Contact No. (required)"
                                            name="contact"
                                            id="contact"
                                            type="text"
                                            {...register("contact", { required: true })}
                                            error={errors.contact}
                                        />
                                        {errors.contact && (
                                            <span className="text-red-500 text-xs">
                                                {errors.contact.message}
                                            </span>
                                        )}
                                    </div>

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Project Date"
                                            name="projectDate"
                                            id="projectDate"
                                            type="date"
                                            defaultValue={defaultValue}
                                            {...register("projectDate")}
                                        />
                                    </div>

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Quotation Date"
                                            name="quotationDate"
                                            id="quotationDate"
                                            type="date"
                                            {...register("quotationDate")}
                                        />
                                    </div>

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Project Name (required)"
                                            name="projectName"
                                            id="projectName"
                                            type="text"
                                            {...register("name", { required: true })}
                                            error={errors.name}
                                        />
                                        {errors.name && (
                                            <span className="text-red-500 text-xs">
                                                {errors.name.message}
                                            </span>
                                        )}
                                    </div>

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            options={projectLocations.map(function (pi) {
                                                return { value: pi.locationCode, data: pi.location };
                                            })}
                                            elementtype="select"
                                            addBlankOption={true}
                                            label="Project Location (required)"
                                            name="projectLocation"
                                            id="projectLocation"
                                            type="text"
                                            {...register(
                                                "projectLocation",
                                                {
                                                    onChange: (e) =>
                                                        handleProjectLocationChange(e.target.value),
                                                },
                                                { required: true }
                                            )}
                                            error={errors.projectLocation}
                                        />

                                        {errors.projectLocation && (
                                            <span className="text-red-500 text-xs">
                                                {errors.projectLocation.message}
                                            </span>
                                        )}
                                    </div>

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            options={cities.map(function (pi) {
                                                return { value: pi.city1, data: pi.city1 };
                                            })}
                                            elementtype="combobox"
                                            addBlankOption={true}
                                            label="City"
                                            name="city"
                                            id="city"
                                            type="text"
                                            listId="cityList"
                                            {...register("city")}
                                        />
                                    </div>

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            options={projectStatus.map(function (pi) {
                                                return { value: pi.id, data: pi.description };
                                            })}
                                            elementtype="select"
                                            addBlankOption={true}
                                            label="Project Status (required)"
                                            name="projectStatus"
                                            id="projectStatus"
                                            type="text"
                                            {...register("projectStatus", { required: true })}
                                            error={errors.projectStatus}
                                        />
                                        {errors.projectStatus && (
                                            <span className="text-red-500 text-xs">
                                                {errors.projectStatus.message}
                                            </span>
                                        )}
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            options={poStatus.map(function (pi) {
                                                return { value: pi.id, data: pi.description };
                                            })}
                                            elementtype="select"
                                            addBlankOption={true}
                                            label="PO Status"
                                            name="poStatus"
                                            id="poStatus"
                                            type="text"
                                            {...register("poStatus")}
                                        />
                                    </div>
                                    {pOStatus == "LOST" && (
                                        <div className="py-1 sm:px-3">
                                            <Input
                                                options={lostReasons.map(function (pi) {
                                                    return { value: pi.id, data: pi.description };
                                                })}
                                                elementtype="select"
                                                addBlankOption={true}
                                                label="Reason For Lost"
                                                name="reasonForLost"
                                                id="reasonForLost"
                                                type="text"
                                                {...register("reasonForLost")}
                                            />
                                        </div>
                                    )}
                                    {reasonForLost == "LOST TO COMPETITION" ||
                                        (true && (
                                            <div className="py-1 sm:px-3">
                                                <Input
                                                    options={competitors.map(function (pi) {
                                                        return { value: pi.id, data: pi.description };
                                                    })}
                                                    elementtype="select"
                                                    addBlankOption={true}
                                                    label="Competitor"
                                                    name="competitor"
                                                    id="competitor"
                                                    type="text"
                                                    {...register("lostCompetitor")}
                                                />
                                            </div>
                                        ))}

                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Client"
                                            name="client"
                                            id="client"
                                            type="text"
                                            {...register("client")}
                                        />
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            options={consultants.map(function (pi) {
                                                return { value: pi.consultant1, data: pi.consultant1 };
                                            })}
                                            elementtype="combobox"
                                            addBlankOption={true}
                                            label="Consultant (required)"
                                            name="consultant"
                                            id="consultant"
                                            type="text"
                                            listId="consultantList"
                                            {...register("consultant", { required: true })}
                                            error={errors.consultant}
                                        />
                                        {errors.consultant && (
                                            <span className="text-red-500 text-xs">
                                                {errors.consultant.message}
                                            </span>
                                        )}
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Main Contractor"
                                            name="mainContractor"
                                            id="mainContractor"
                                            type="text"
                                            {...register("mainContractor")}
                                        />
                                    </div>

                                    {/* Application Engineer */}
                                    <div className="pt-3 pb-1 sm:px-3">
                                        <h3 className="text-sm font-semibold text-gray-700">
                                            Application Engineer
                                        </h3>
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Name"
                                            name="applicationEngineerName"
                                            id="applicationEngineerName"
                                            type="text"
                                            {...register("applicationEngineer.name")}
                                        />
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Tel/Mob No."
                                            name="applicationEngineerTelephone"
                                            id="applicationEngineerTelephone"
                                            type="text"
                                            {...register("applicationEngineer.telephone")}
                                        />
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Email"
                                            name="applicationEngineerEmail"
                                            id="applicationEngineerEmail"
                                            type="text"
                                            {...register("applicationEngineer.email")}
                                        />
                                    </div>

                                    {/* Sales Engineer */}
                                    <div className="pt-3 pb-1 sm:px-3">
                                        <h3 className="text-sm font-semibold text-gray-700">
                                            Sales Engineer
                                        </h3>
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            options={salesEngineers.map(function (se) {
                                                return { value: se.name, data: se.name };
                                            })}
                                            elementtype="select"
                                            addBlankOption={true}
                                            label="Name (required)"
                                            name="salesEngineerName"
                                            id="salesEngineerName"
                                            type="text"
                                            {...register("salesEngineer.name", {
                                                onChange: (e) =>
                                                    handleSalesEngineerChange(e.target.value),
                                                required: true,
                                            })}
                                            error={errors.salesEngineer?.name}
                                        />
                                        {errors.salesEngineer?.name && (
                                            <span className="text-red-500 text-xs">
                                                {errors.salesEngineer.name.message}
                                            </span>
                                        )}
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Tel/Mob No."
                                            name="salesEngineerTelephone"
                                            id="salesEngineerTelephone"
                                            type="text"
                                            {...register("salesEngineer.telephone")}
                                        />
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Email"
                                            name="salesEngineerEmail"
                                            id="salesEngineerEmail"
                                            type="text"
                                            {...register("salesEngineer.email")}
                                        />
                                    </div>

                                    <div className="pt-3 pb-1 sm:px-3">
                                        <h3 className="text-sm font-semibold text-gray-700">
                                            Sales Manager
                                        </h3>
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Name"
                                            name="salesManagerName"
                                            id="salesManagerName"
                                            type="text"
                                            {...register("salesManager.name")}
                                        />
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Tel/Mob No."
                                            name="salesManagerTelephone"
                                            id="salesManagerTelephone"
                                            type="text"
                                            {...register("salesManager.telephone")}
                                        />
                                    </div>
                                    <div className="py-1 sm:px-3">
                                        <Input
                                            elementtype="input"
                                            label="Email"
                                            name="salesManagerEmail"
                                            id="salesManagerEmail"
                                            type="text"
                                            {...register("salesManager.email")}
                                        />
                                    </div>
                                </div>
                            </Tabs>
                        </div>
                    </div>
                </div>
                <div className="col-span-3 overflow-hidden rounded-lg bg-white shadow text-right">
                    <ProjectHeader
                        saveProject={saveProjectHandler}
                        saveAsDraft={saveAsDraftHandler}
                        getProjects={openProjectListModal}
                        onNewProject={handleNewProject}
                        onPrintSummary={onPrintSummary}
                        onPrint={onPrint}
                        isLoading={printing || isFetchinGetProjectItems || isSavingDraft || isImportingDraft}
                        openDiscountModal={openDiscountModal}
                        openCustomItemModal={openCustomItemModal}
                        onPrintClick={onPrintClick}
                        onRefreshPrice={onRefreshPrice}
                        onOpenDraftFile={triggerOpenDraftFile}
                        onOpenOfferModal={openOfferModal}
                        onEngineerNotes={() => setShowNotesModal(true)}
                        notesCount={engineerNotes?.length || 0}
                    ></ProjectHeader>

                    <ProjectItemsGrid
                        items={projectItemsSummary?.projectResultItems}
                        totalQuantity={projectItemsSummary?.totalQuantity}
                        totalPrice={projectItemsSummary?.totalPriceText}
                        discountLimit={user?.userGroupId == 2 ? null : discountLimit}
                        user={user}
                        priceBreakdown={projectItemsSummary?.priceBreakdown}
                        project={project}
                        updateProjectAddons={updateProjectAddons}
                        refreshProjectPrice={refreshProjectPrice}
                        lockDiscount={hasProjectDiscount}
                    />
                </div>

                <Modal
                    isOpen={modalIsOpen}
                    onRequestClose={closeModal}
                    contentLabel="Project List Modal"
                >
                    <ProjectLists
                        projectLists={projectLists}
                        onOpenProject={handleOpenProject}
                        user={user}
                    ></ProjectLists>
                </Modal>

                <Modal
                    shouldCloseOnOverlayClick={true}
                    style={projectDiscountStyle}
                    isOpen={discountModalIsOpen}
                    onRequestClose={closeModal}
                    contentLabel="Discount Modal"
                >
                    <ProjectDiscount
                        closeDiscount={() => closeModal()}
                        projectDiscount={projectDiscount}
                        salesOfficeMargin={salesOfficeMargin}
                        setProjectDiscount={setProjectDiscount}
                        setSalesOfficeMargin={setSalesOfficeMargin}
                        updateProjectDiscounts={updateProjectDiscounts}
                        discountLimit={user.userGroupId == 2 ? 0 : discountLimit}
                        lockDiscount={hasLineItemDiscount}
                    ></ProjectDiscount>
                </Modal>
                <Modal
                    shouldCloseOnOverlayClick={true}
                    style={customItemStyle}
                    isOpen={customItemModalIsOpen}
                    onRequestClose={closeModal}
                    contentLabel="Discount Modal"
                >
                    <CustomItem
                        closeModal={() => closeModal()}
                        customModal={customModal}
                        setCustomModal={setCustomModal}
                        customPrice={customPrice}
                        setCustomPrice={setCustomPrice}
                        customQty={customQty}
                        setCustomQty={setCustomQty}
                        onAddCustomItem={onAddCustomItem}
                    ></CustomItem>
                </Modal>

                <NewModal
                    open={printOptionModalIsOpen}
                    setOpen={setPrintOptionModalIsOpen}
                    title="Print Options"
                >
                    <PrintOptions
                        printOptions={printOptions()}
                        selectedPrintOptions={selectedPrintOptions}
                        setSelectedPrintOptions={setSelectedPrintOptions}
                        onChangedHandler={onPrintOptionsChanged}
                        onPrint={onPrint}
                    ></PrintOptions>
                </NewModal>
                <OfferModal
                    isOpen={offerModalIsOpen}
                    onClose={closeOfferModal}
                    offerData={offerData}
                    selectedOffers={selectedOffers}
                    onSave={handleSaveOffers}
                />
            </div>

            <EngineerNotesModal
                isOpen={showNotesModal}
                onClose={() => setShowNotesModal(false)}
                notes={engineerNotes}
                onSave={(notes) =>
                    setValue("engineerNotes", notes, { shouldDirty: true })
                }
            />
        </form>
    );
};

export default Project;